import React, { useState } from "react";
import { useShow, useCustomMutation } from "@refinedev/core";
import { DateField } from "@refinedev/antd";
import { Typography, Tag, Table, Card, Space, Button, Badge, Modal, Form, Select, Input, message } from "antd";
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    EditOutlined,
    FileTextOutlined,
    SyncOutlined,
    PrinterOutlined,
    ClockCircleOutlined,
    DollarOutlined
} from "@ant-design/icons";
import { Link, useNavigate, useParams } from "react-router-dom";
import { invoiceRegenerationService, RegenerationRequest } from "../../api/invoiceRegenerationService";
import { PageHeader } from "../../components/ui";

const { Text } = Typography;
const { TextArea } = Input;

interface ActionButton {
    label: string;
    onClick: () => void;
    type?: "primary" | "default" | "dashed" | "text" | "link";
    icon?: React.ReactNode;
    danger?: boolean;
    ghost?: boolean;
    key?: string;
}

export const SaleOrderShow: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [regenerateModalVisible, setRegenerateModalVisible] = useState(false);
    const [regenerating, setRegenerating] = useState(false);
    const [form] = Form.useForm();

    const { queryResult } = useShow({
        resource: "saleorders",
        meta: {
            fields: [
                "id",
                "orderNumber",
                "customerTPIN",
                "customerId",
                "customerName",
                "currencyTypeCode",
                "exchangeRate",
                "subTotal",
                "totalTaxableAmount",
                "totalTax",
                "totalAmount",
                "status",
                "orderDate",
                "deliveryDate",
                "documentUrl",
                "createdAt",
                "updatedAt",
                {
                    items: [
                        "id",
                        "inventoryItemId",
                        "itemCode",
                        "itemName",
                        "itemClassCode",
                        "quantity",
                        "quantityUnitCode",
                        "unitPrice",
                        "lineAmount",
                        "taxRate",
                        "taxAmount",
                        "totalAmount"
                    ]
                },
                {
                    billableServices: [
                        "id",
                        "description",
                        "price",
                        "taxId",
                        "taxRate",
                        "taxType",
                        "tax"
                    ]
                }
            ]
        }
    });
    const { data, isLoading } = queryResult;
    const record = data?.data;

    const { mutate: confirm } = useCustomMutation();
    const { mutate: cancel } = useCustomMutation();
    const { mutate: convert } = useCustomMutation();
    const { mutate: generatePdf } = useCustomMutation();

    const getStatusColor = (status: string) => {
        switch (status) {
            case "Confirmed":
                return "green";
            case "Draft":
                return "blue";
            case "Converted":
                return "purple";
            case "Cancelled":
                return "red";
            default:
                return "default";
        }
    };

    const handleConfirm = () => {
        confirm({
            url: `SaleOrders/${record?.id}/confirm`,
            method: "post",
            successNotification: () => ({
                message: "Success",
                description: "Proforma invoice confirmed successfully",
                type: "success",
            }),
        }, {
            onSuccess: () => {
                queryResult.refetch();
            }
        });
    };

    const handleCancel = () => {
        cancel({
            url: `SaleOrders/${record?.id}/cancel`,
            method: "post",
            successNotification: () => ({
                message: "Success",
                description: "Proforma invoice cancelled successfully",
                type: "success",
            }),
        }, {
            onSuccess: () => {
                queryResult.refetch();
            }
        });
    };

    const handleConvert = () => {
        convert({
            url: `SaleOrders/${record?.id}/convert`,
            method: "post",
            successNotification: () => ({
                message: "Success",
                description: "Proforma invoice converted to sale successfully",
                type: "success",
            }),
        }, {
            onSuccess: () => {
                queryResult.refetch();
            }
        });
    };

    const handleViewPdf = () => {
        generatePdf({
            url: `SaleOrders/${record?.id}/pdf`,
            method: "get",
            successNotification: false,
        }, {
            onSuccess: (data: any) => {
                const documentUrl = data?.data?.documentUrl || data?.documentUrl;
                if (documentUrl) {
                    queryResult.refetch();
                    window.open(documentUrl, '_blank');
                } else {
                    message.error("Failed to get document URL");
                }
            },
            onError: () => {
                message.error("Failed to generate or retrieve PDF");
            },
        });
    };

    const handleRegenerateSubmit = async (values: any) => {
        if (!record) return;

        setRegenerating(true);
        try {
            const request: RegenerationRequest = {
                regenerationType: values.regenerationType,
                reason: values.reason,
                preserveOriginal: true,
                notifyCustomer: false,
            };

            const result = await invoiceRegenerationService.regenerateSaleOrder(record.id, request);

            if (result.success) {
                message.success(`Sale order ${record.orderNumber} regenerated successfully!`);
                setRegenerateModalVisible(false);
                queryResult.refetch();
            } else {
                message.error(`Failed to regenerate sale order: ${result.errorMessage}`);
            }
        } catch (error: any) {
            console.error("Sale order regeneration error:", error);
            message.error(`Failed to regenerate sale order: ${error.response?.data?.message || error.message}`);
        } finally {
            setRegenerating(false);
        }
    };

    const handleModalCancel = () => {
        setRegenerateModalVisible(false);
        form.resetFields();
    };

    const displayCustomerTPIN = (tpin: string) => {
        if (!tpin || tpin === "0") {
            return <Text type="secondary" italic>Manual Entry</Text>;
        }
        return tpin;
    };

    const formatCurrency = (value: number | undefined) => {
        if (value === undefined || value === null) return "0.00";
        return value.toFixed(2);
    };

    const itemColumns: any[] = [
        {
            title: 'Item Code',
            dataIndex: 'itemCode',
            key: 'itemCode',
            render: (text: string, record: any) => {
                const inventoryItemId = record?.inventoryItemId;
                return inventoryItemId ? (
                    <Link to={`/products/show/${inventoryItemId}`} style={{ color: 'var(--color-primary)' }}>
                        {text}
                    </Link>
                ) : text;
            }
        },
        {
            title: 'Item Name',
            dataIndex: 'itemName',
            key: 'itemName',
            render: (text: string, record: any) => {
                const inventoryItemId = record?.inventoryItemId;
                return inventoryItemId ? (
                    <Link to={`/products/show/${inventoryItemId}`} style={{ color: 'var(--color-primary)' }}>
                        {text}
                    </Link>
                ) : text;
            }
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            render: (value: number) => value?.toFixed(2)
        },
        {
            title: 'Unit Price',
            dataIndex: 'unitPrice',
            key: 'unitPrice',
            render: (value: number, record: any) => {
                const totalAmount = Number(record?.totalAmount) || 0;
                const quantity = Number(record?.quantity) || 1;
                const taxInclusiveUnitPrice = totalAmount / quantity;
                return taxInclusiveUnitPrice.toFixed(2);
            }
        },
        {
            title: 'Tax Rate',
            dataIndex: 'taxRate',
            key: 'taxRate',
            render: (value: number) => `${value?.toFixed(2)}%`
        },
        {
            title: 'Tax Amount',
            dataIndex: 'taxAmount',
            key: 'taxAmount',
            render: (value: number) => value?.toFixed(2)
        },
        {
            title: 'Total Amount',
            dataIndex: 'totalAmount',
            key: 'totalAmount',
            render: (value: number) => value?.toFixed(2)
        }
    ];

    const billableServicesColumns: any[] = [
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description'
        },
        {
            title: 'Price',
            dataIndex: 'price',
            key: 'price',
            render: (value: number) => value?.toFixed(2)
        },
        {
            title: 'Tax Rate',
            dataIndex: 'taxRate',
            key: 'taxRate',
            render: (value: number) => `${value?.toFixed(2)}%`
        },
        {
            title: 'Tax Amount',
            dataIndex: 'taxAmount',
            key: 'taxAmount',
            render: (value: any, record: any) => {
                const price = Number(record.price) || 0;
                const taxRate = (Number(record.taxRate) || 0) / 100;
                const taxAmount = price - (price / (1 + taxRate));
                return taxAmount.toFixed(2);
            }
        },
        {
            title: 'Total Amount',
            dataIndex: 'totalAmount',
            key: 'totalAmount',
            render: (value: any, record: any) => {
                const price = Number(record.price) || 0;
                return price.toFixed(2);
            }
        }
    ];

    // Build actions array
    const actions: ActionButton[] = [
        {
            label: "Print",
            onClick: handleViewPdf,
            type: "default",
            icon: <PrinterOutlined />,
            key: "print",
        },
        {
            label: "Edit",
            onClick: () => navigate(`/sale-orders/edit/${id}`),
            type: "default",
            icon: <EditOutlined />,
            key: "edit",
        },
    ];

    if (record?.documentUrl) {
        actions.push({
            label: "Regenerate",
            onClick: () => setRegenerateModalVisible(true),
            type: "default",
            icon: <SyncOutlined />,
            key: "regenerate",
        });
    }

    if (record?.status === "Draft") {
        actions.push({
            label: "Confirm",
            onClick: handleConfirm,
            type: "primary",
            icon: <CheckCircleOutlined />,
            key: "confirm",
        });
    }

    if (record?.status === "Confirmed") {
        actions.push({
            label: "Convert to Sale",
            onClick: handleConvert,
            type: "primary",
            icon: <DollarOutlined />,
            key: "convert",
        });
    }

    // Build more menu items
    const moreMenuItems: ActionButton[] = [];

    if (record?.status === "Draft" || record?.status === "Confirmed") {
        moreMenuItems.push({
            key: "cancel",
            label: "Cancel Proforma",
            onClick: handleCancel,
            icon: <CloseCircleOutlined />,
            danger: true,
        });
    }

    if (isLoading) {
        return <div className="page-container">Loading...</div>;
    }

    return (
        <div className="page-container">
            <PageHeader
                title={`Proforma Invoice #${record?.orderNumber || id}`}
                titleIcon={<FileTextOutlined />}
                breadcrumbs={[
                    { title: "Proforma Invoices", href: "/sale-orders" },
                    { title: `#${record?.orderNumber || id}` },
                ]}
                actions={actions}
                moreMenuItems={moreMenuItems.length > 0 ? moreMenuItems : undefined}
            >
                <div className="mini-stat-cards-row">
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Total Amount</div>
                        <div className="mini-stat-value">${formatCurrency(record?.totalAmount)}</div>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Status</div>
                        <div className={`status-badge ${record?.status === "Confirmed" ? "good" : record?.status === "Draft" ? "pending" : record?.status === "Converted" ? "good" : "inactive"}`}>
                            {record?.status === "Confirmed" && <CheckCircleOutlined />}
                            {record?.status === "Draft" && <ClockCircleOutlined />}
                            {record?.status === "Converted" && <CheckCircleOutlined />}
                            {record?.status === "Cancelled" && <CloseCircleOutlined />}
                            {record?.status}
                        </div>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Currency</div>
                        <div className="mini-stat-value">{record?.currencyTypeCode}</div>
                    </div>
                </div>
            </PageHeader>

            {/* Proforma Details */}
            <div className="info-cards-grid">
                <div className="info-card">
                    <div className="info-card-header">Proforma Details</div>
                    <div className="info-card-content">
                        <div className="info-item">
                            <span className="info-label">Proforma Number:</span>
                            <span className="info-value">{record?.orderNumber || '-'}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Status:</span>
                            <span className="info-value">
                                <Tag color={getStatusColor(record?.status)}>{record?.status}</Tag>
                            </span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Order Date:</span>
                            <span className="info-value"><DateField value={record?.orderDate} format="YYYY-MM-DD" /></span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Delivery Date:</span>
                            <span className="info-value"><DateField value={record?.deliveryDate} format="YYYY-MM-DD" /></span>
                        </div>
                    </div>
                </div>

                <div className="info-card">
                    <div className="info-card-header">Customer Details</div>
                    <div className="info-card-content">
                        <div className="info-item">
                            <span className="info-label">Customer Name:</span>
                            <span className="info-value">
                                {record?.customerId ? (
                                    <Link to={`/customers/show/${record.customerId}`} style={{ color: 'var(--color-primary)' }}>
                                        {record?.customerName}
                                    </Link>
                                ) : (
                                    record?.customerName || '-'
                                )}
                            </span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Customer TPIN:</span>
                            <span className="info-value">{displayCustomerTPIN(record?.customerTPIN)}</span>
                        </div>
                    </div>
                </div>

                <div className="info-card">
                    <div className="info-card-header">Financial Summary</div>
                    <div className="info-card-content">
                        <div className="info-item">
                            <span className="info-label">Subtotal:</span>
                            <span className="info-value">${formatCurrency((record?.totalAmount || 0) - (record?.totalTax || 0))}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Total Tax:</span>
                            <span className="info-value">${formatCurrency(record?.totalTax)}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Total Amount:</span>
                            <span className="info-value" style={{ fontWeight: 'bold', color: 'var(--color-primary)' }}>
                                ${formatCurrency(record?.totalAmount)}
                            </span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Exchange Rate:</span>
                            <span className="info-value">{record?.exchangeRate?.toFixed(4)}</span>
                        </div>
                    </div>
                </div>

                <div className="info-card">
                    <div className="info-card-header">System Information</div>
                    <div className="info-card-content">
                        <div className="info-item">
                            <span className="info-label">Created At:</span>
                            <span className="info-value"><DateField value={record?.createdAt} format="YYYY-MM-DD HH:mm" /></span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Updated At:</span>
                            <span className="info-value"><DateField value={record?.updatedAt} format="YYYY-MM-DD HH:mm" /></span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Proforma Items */}
            <div className="data-table-card">
                <Card
                    title={
                        <Space>
                            <span>Proforma Items</span>
                            <Badge count={record?.items?.length || 0} style={{ backgroundColor: 'var(--color-primary)' }} />
                        </Space>
                    }
                >
                    {record?.items && record.items.length > 0 ? (
                        <Table
                            dataSource={record?.items}
                            columns={itemColumns}
                            rowKey={(record) => record.id}
                            pagination={false}
                            className="custom-table"
                            scroll={{ x: 800 }}
                        />
                    ) : (
                        <Text type="secondary">No items available</Text>
                    )}
                </Card>
            </div>

            {/* Billable Services */}
            {record?.billableServices && record.billableServices.length > 0 && (
                <div className="data-table-card">
                    <Card
                        title={
                            <Space>
                                <span>Billable Services</span>
                                <Badge count={record?.billableServices?.length || 0} style={{ backgroundColor: 'var(--color-primary)' }} />
                            </Space>
                        }
                    >
                        <Table
                            dataSource={record?.billableServices}
                            columns={billableServicesColumns}
                            rowKey={(record) => record.id}
                            pagination={false}
                            className="custom-table"
                            scroll={{ x: 600 }}
                        />
                    </Card>
                </div>
            )}

            {/* Regenerate Modal */}
            <Modal
                title="Regenerate Proforma Invoice"
                open={regenerateModalVisible}
                onCancel={handleModalCancel}
                footer={null}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleRegenerateSubmit}
                >
                    <Form.Item
                        name="regenerationType"
                        label="Regeneration Type"
                        rules={[{ required: true, message: 'Please select a regeneration type' }]}
                    >
                        <Select placeholder="Select regeneration type">
                            <Select.Option value="BugFix">Bug Fix</Select.Option>
                            <Select.Option value="DataCorrection">Data Correction</Select.Option>
                            <Select.Option value="FormatUpdate">Format Update</Select.Option>
                            <Select.Option value="CustomerRequest">Customer Request</Select.Option>
                            <Select.Option value="Other">Other</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="reason"
                        label="Reason"
                        rules={[{ required: true, message: 'Please provide a reason' }]}
                    >
                        <TextArea
                            rows={4}
                            placeholder="Enter reason for regeneration..."
                        />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={regenerating}>
                                Regenerate
                            </Button>
                            <Button onClick={handleModalCancel}>
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};
